/**
 * `<page-validation>` — the proxied validation surface for one instance, routed
 * at `/instances/:id/validation`.
 *
 * It runs `sluice config validate` and `sluice policy validate` inside a
 * one-shot task on the instance's **pinned image** and renders the merged report
 * (`SC-F700`, `SC-F701`, `SC-F702`, `SC-F703`, `SC-F332`). It is also the screen
 * a blocked publish sends a maintainer to, because the validation half of the
 * pre-publish gate is not optional (`SC-F709`).
 *
 * Stories: `SC-S700`, `SC-S701`, `SC-S702`, `SC-S703`, `SC-S704`, `SC-S713`.
 *
 * **Four things here are honesty requirements rather than presentation
 * choices**, and each one is a place where a tidier screen would lie:
 *
 *  1. **A failing bundle is a SUCCEEDED job.** Exit 3 means the validator ran
 *     correctly and the answer is no (`06` §4.3). The report is red and the run
 *     is reported as having worked, because "the job failed" would blame the
 *     validator for the bundle and would hide the findings — a failed job
 *     carries an error envelope and no result.
 *  2. **There are no line numbers, ever.** The error format carries one and
 *     Sluice's loader never populates it, so `line` is null on every finding.
 *     The page says so once and anchors findings to objects, because a UI that
 *     showed "line 1" would send an author to the wrong place.
 *  3. **Compile-stage findings anchor to the bundle, not to a field.** They are
 *     unstructured strings with no file, no kind and no field path, and the
 *     paths inside them are the engine's (`resource.tags`), not the manifest's.
 *     They are rendered in their own section with that stated, never dropped and
 *     never guessed at.
 *  4. **`stage_reached` is rendered next to the findings.** The stages are
 *     sequential and mutually exclusive inside one command, so "no compile
 *     findings" can mean the compiler never ran. Silence about that reads as a
 *     clean compile over a bundle that never reached the compiler.
 *
 * Output the parser did not recognise is shown verbatim with a warning, because
 * a parser that answered "no findings" for output it did not understand would
 * turn a validation failure into a green light (`SC-S704`).
 *
 * **It also hosts `SC-F715`, query replay.** Validation asks whether a bundle is
 * well-formed; a replay asks what that bundle would have decided about traffic
 * that actually happened, using the decision recorded in the audit trail as the
 * baseline. It is on this screen and not on one of its own so that there is one
 * target picker rather than two that can disagree about which bundle is being
 * examined. Four limits are rendered rather than paraphrased, because they come
 * from the API and a client that softened them would be the thing that misleads:
 * nothing is executed; API-key traffic cannot be modeled at all (`SLUICE-U27`)
 * and is reported as skipped rather than as unchanged; the recorded statement is a
 * clipped sample and the record names no configuration, so a difference is
 * evidence and not proof; and the selection is bounded by a window and a cap whose
 * ordering rule the report publishes.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type { BnBadgeVariant, BnKvRow, BnSelectChangeDetail, BnSelectOption } from "../components/index.js";

import { me } from "../api/auth.js";
import {
  replayQueries,
  validate,
  type ReplayReport,
  type ReplayScenario,
  type TestRunResult,
  type ValidateCommand,
  type ValidateResult,
  type ValidationFinding,
  type ValidationStage,
  type ValidationTarget,
} from "../api/cli.js";
import { ApiError, parseError, validationFields } from "../api/errors.js";
import { getInstance, type Instance } from "../api/instances.js";
import { cancelJob, isTerminal, jobFailure, watchJob, type Job } from "../api/jobs.js";
import { atLeast, type Role } from "../api/orgs.js";
import { hrefFor, navigate } from "../nav.js";

/** How long each long-poll is held open. `06` §1.8 caps it at 30 s server-side;
 * 20 leaves room for the round trip. The first POST carries the same value, so a
 * validate that finishes inside it needs no poll at all. */
const WAIT_SECONDS = 20;

/** The four stages, in the order a bundle passes through them, with what each
 * one proves and — the load-bearing half — what a later stage's silence does
 * **not** prove. */
const STAGES: Record<ValidationStage, { label: string; meaning: string }> = {
  server_config: {
    label: "Server configuration",
    meaning:
      "The rendered server configuration was loaded. A failure here returns before the policy directory is read at all, so an empty policy finding list means the policies were never looked at.",
  },
  decode: {
    label: "Decode",
    meaning:
      "Every manifest in the policy directory was parsed and type-checked. A decode failure returns before the compiler runs, so no compile findings does not mean the compiler was happy.",
  },
  compile: {
    label: "Compile",
    meaning:
      "The snapshot compiler ran, which is the only stage that catches a manifest that decodes cleanly and uses a feature this build refuses. Compile findings carry no file, kind or field path.",
  },
  cross_check: {
    label: "Cross-check",
    meaning:
      "The whole-bundle checks ran — the ones that need the server configuration and the policy directory together, such as an ApprovalPolicy without a public base URL.",
  },
};

/** The target picker. A generation number is typed into the field beside it. */
const TARGET_OPTIONS: BnSelectOption[] = [
  {
    value: "draft",
    label: "Draft",
    description: "The working set — what every authoring surface in the product edits.",
  },
  {
    value: "live",
    label: "Live generation",
    description: "The bundle the instance is serving now. Refused if nothing has been published.",
  },
  {
    value: "generation",
    label: "A generation number",
    description: "A bundle from the history, which is what makes an old decision reproducible.",
  },
];

/**
 * The replay windows (`SC-F715`). Seven days is the API's ceiling and the reason is
 * worth carrying into the label: a replay has to read the recorded statement of
 * every candidate record, which is the one field no column index can answer.
 */
const REPLAY_WINDOWS: Record<string, { label: string; ms: number }> = {
  "1h": { label: "Last hour", ms: 60 * 60 * 1000 },
  "24h": { label: "Last 24 hours", ms: 24 * 60 * 60 * 1000 },
  "7d": { label: "Last 7 days (the maximum)", ms: 7 * 24 * 60 * 60 * 1000 },
};

const REPLAY_WINDOW_OPTIONS: BnSelectOption[] = Object.entries(REPLAY_WINDOWS).map(
  ([value, { label }]) => ({ value, label }),
);

/** The scenario cap. 200 is the API's ceiling — the same number `06` §4.5.1 puts on
 * the explain matrix, because both are "how much policy evaluation fits in one
 * task". A lower cap is faster and drops more, and the report says which scenarios
 * a truncation dropped. */
const REPLAY_CAP_OPTIONS: BnSelectOption[] = [
  { value: "25", label: "25 scenarios" },
  { value: "50", label: "50 scenarios" },
  { value: "200", label: "200 scenarios (the maximum)" },
];

/**
 * The human labels for the skip reasons. Each one says what cannot be answered
 * rather than naming a code, because these counts are the honest half of the report
 * and a reader should not have to look anything up to read them.
 */
const SKIP_LABELS: Record<string, string> = {
  api_key_subject:
    "Authenticated with an API key — cannot be modeled at all, because a generated case is JWT-shaped (SLUICE-U27)",
  not_a_subject_query: "Not a subject's query — an admin-plane action or an unauthenticated request",
  no_recorded_sql: "No recorded statement — the audit profile or the projection withheld it",
  no_recorded_subject: "No recorded subject identifier",
  recorded_decision_not_comparable:
    "The recorded outcome has no expectation to compare against — an error rather than a policy decision",
  over_the_scenario_cap: "Past the scenario cap — the least frequent scenarios are dropped first",
};

/**
 * `<page-validation>` runs the two proxied validators over one bundle and renders
 * the merged report.
 *
 * Implements `SC-F701` (`config validate`), `SC-F702` (`policy validate`),
 * `SC-F703` (the output parser's findings, anchored to objects where the message
 * named one and to the bundle where it could not), `SC-F700` (the one-shot task
 * on the pinned image) and `SC-F332` (the job it is watched through). It is the
 * screen `SC-F709`'s publish gate sends a blocked publish to.
 *
 * Stories: `SC-S700`, `SC-S701`, `SC-S702`, `SC-S703`, `SC-S704`, `SC-S713`.
 *
 *   <page-validation .instanceId=${id}></page-validation>
 */
@customElement("page-validation")
export class PageValidation extends LitElement {
  @property() instanceId = "";

  /** The instance, for the header and the pinned version. */
  @state() private instance: Instance | null = null;
  @state() private loading = true;
  @state() private loadError: ApiError | null = null;

  /** The form. */
  @state() private targetKind: "draft" | "live" | "generation" = "draft";
  @state() private generation = "";
  @state() private commands: ValidateCommand[] = ["config", "policy"];
  /** Field-level refusal on the target, from a `422`'s `details.fields`. */
  @state() private targetError = "";

  /** The run. `job` is the last observation, so the panel can show the step and
   * offer cancellation; `result` is the report; `runError` is either a failure to
   * start or a job that reached a non-success terminal state. */
  @state() private job: Job<ValidateResult> | null = null;
  @state() private result: ValidateResult | null = null;
  @state() private runError: ApiError | null = null;
  @state() private running = false;

  /**
   * `SC-F715`'s panel — replay recorded queries against a candidate policy set.
   *
   * It lives on this screen rather than on its own because it answers the same
   * question the validators answer, one step further out: "would this bundle
   * break anything?" — validation asks whether the bundle is well-formed, and a
   * replay asks what it would have decided about traffic that actually happened.
   * The candidate is the target picked above, so there is one target on the screen
   * rather than two that can disagree.
   */
  @state() private orgRole: Role | undefined;
  @state() private replayWindow = "24h";
  @state() private replayCap = "50";
  @state() private replayJob: Job<TestRunResult> | null = null;
  @state() private replayResult: TestRunResult | null = null;
  @state() private replayError: ApiError | null = null;
  @state() private replaying = false;

  private abort?: AbortController;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      margin-bottom: var(--bino-space-lg);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    .form-row {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
    }
    .form-row bn-field {
      margin-bottom: 0;
    }
    .target {
      width: 16rem;
    }
    .gen {
      width: 10rem;
    }
    .checks {
      display: grid;
      gap: var(--bino-space-sm);
      margin: var(--bino-space-md) 0;
      max-width: 70ch;
    }
    .actions {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      max-width: 80ch;
    }
    p:last-child {
      margin-bottom: 0;
    }
    .muted {
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-sm);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .verdict {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-md);
    }
    .verdict-line {
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .notes {
      display: grid;
      gap: var(--bino-space-sm);
    }
    .finding {
      border-top: 1px solid var(--bino-border-light);
      padding: var(--bino-space-md) 0;
      display: grid;
      gap: var(--bino-space-xs);
    }
    .finding:first-of-type {
      border-top: none;
      padding-top: 0;
    }
    .finding-head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .chip {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      padding: 0 var(--bino-space-xs);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-surface-inset);
      color: var(--bino-text-muted);
    }
    .finding-message {
      font-size: var(--bino-font-size-md);
    }
    .finding-raw {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      overflow-x: auto;
      white-space: pre;
      max-width: 100%;
    }
    a.inline {
      color: var(--bino-primary);
      font-weight: 600;
      text-decoration: none;
    }
    a.inline:hover {
      text-decoration: underline;
    }
    a.inline:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    /* SC-F715's panel. */
    .replay-error {
      display: grid;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
      padding: var(--bino-space-md);
      border: 1px solid var(--bino-warning-border);
      background: var(--bino-warning-bg);
      border-radius: var(--bino-radius-sm);
    }
    .sql {
      display: block;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius-sm);
      padding: var(--bino-space-xs);
      overflow-x: auto;
      white-space: pre;
    }
    .limits {
      margin: var(--bino-space-sm) 0 0;
      padding-left: var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .limits li {
      margin-bottom: var(--bino-space-xs);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Abandoning the watch does not cancel the job: a validate is read-only and
    // 90-day retained, so it stays readable on the jobs tab.
    this.abort?.abort();
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has("instanceId") && changed.get("instanceId") !== undefined) void this.load();
  }

  private async load() {
    if (!this.instanceId) return;
    this.loading = true;
    try {
      this.instance = await getInstance(this.instanceId);
      this.loadError = null;
      void this.loadRole();
    } catch (err) {
      this.loadError = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /**
   * loadRole resolves the caller's role on this instance's organization, for the
   * replay panel only.
   *
   * An undefined role means the lookup failed, and the panel then OFFERS the run
   * rather than hiding it: the API is the authority and its refusal names the rule,
   * where a hidden control would leave a maintainer wondering where the feature
   * went. Hiding is an affordance, never the enforcement.
   */
  private async loadRole() {
    const org = this.instance?.org;
    if (!org) return;
    try {
      const identity = await me();
      this.orgRole = identity.organizations.find((o) => o.id === org)?.role;
    } catch {
      this.orgRole = undefined;
    }
  }

  /* ── the run ───────────────────────────────────────────────────────────── */

  private get target(): ValidationTarget {
    if (this.targetKind === "generation") return Number(this.generation);
    return this.targetKind;
  }

  private get targetValid(): boolean {
    if (this.targetKind !== "generation") return true;
    return /^[0-9]+$/.test(this.generation.trim()) && Number(this.generation) > 0;
  }

  private async run() {
    if (this.commands.length === 0 || !this.targetValid) return;
    this.abort?.abort();
    this.abort = new AbortController();
    this.running = true;
    this.runError = null;
    this.result = null;
    this.job = null;
    this.targetError = "";
    try {
      const started = await validate(
        this.instanceId,
        { target: this.target, commands: this.commands },
        { waitSeconds: WAIT_SECONDS },
      );
      this.job = started.job;
      // `?wait=` may already have carried this to a terminal state, in which case
      // there is nothing to poll for.
      const done = isTerminal(started.job.status)
        ? started.job
        : await watchJob<ValidateResult>(started.job.id, {
            waitSeconds: WAIT_SECONDS,
            onUpdate: (j) => (this.job = j),
            signal: this.abort.signal,
          });
      this.job = done;
      const failure = jobFailure(done);
      if (failure) {
        this.runError = failure;
        return;
      }
      this.result = done.result;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      const parsed = parseError(err);
      this.runError = parsed;
      const field = validationFields(parsed).find((f) => f.path === "target");
      if (field) this.targetError = field.reason;
    } finally {
      this.running = false;
    }
  }

  /** Cancelling a proxied command kills the task, which `06` §3.11 permits at
   * any point for the read-only commands — unlike a publish, where cancelling
   * mid-flight would leave desired and observed state divergent. */
  private async cancel() {
    const id = this.job?.id;
    if (!id) return;
    try {
      await cancelJob(id);
    } catch (err) {
      this.runError = parseError(err);
    }
  }

  private toggleCommand(cmd: ValidateCommand, on: boolean) {
    const next = new Set(this.commands);
    if (on) next.add(cmd);
    else next.delete(cmd);
    // Canonical order — server config first, because a bundle whose server
    // configuration does not load is a publish-path condition and a reader
    // should see that before a page of policy decode errors.
    this.commands = (["config", "policy"] as ValidateCommand[]).filter((c) => next.has(c));
  }

  /* ── rendering ─────────────────────────────────────────────────────────── */

  override render() {
    if (this.loading && !this.instance) {
      return html`<bn-spinner block size="28" label="Loading this instance"></bn-spinner>`;
    }
    if (this.loadError) return this.renderLoadError(this.loadError);
    return html`
      <header>
        <h1>Validation</h1>
        <p class="lede">
          Both validators run inside a one-shot container on this instance's own pinned Sluice image,
          with the bundle mounted read-only — so the answer here is the answer the running binary
          would give. Nothing is published and nothing on the instance is touched.
        </p>
      </header>
      <div class="stack">
        ${this.renderForm()} ${this.renderRunState()} ${this.renderOutcome()}
        ${this.renderReplay()} ${this.renderOtherChecks()}
      </div>
    `;
  }

  /* ── SC-F715: query replay ─────────────────────────────────────────────── */

  /** mayReplay is the affordance, not the control. See [loadRole]. */
  private get mayReplay(): boolean {
    return this.orgRole === undefined || atLeast(this.orgRole, "maintainer");
  }

  /**
   * replay runs the regression simulation over the chosen window.
   *
   * The candidate is the target picked at the top of the screen, so a maintainer
   * validating a draft and replaying against it cannot accidentally be looking at
   * two different bundles.
   */
  private async replay() {
    if (!this.targetValid) return;
    this.replaying = true;
    this.replayError = null;
    this.replayResult = null;
    this.replayJob = null;
    try {
      const to = new Date();
      const from = new Date(to.getTime() - REPLAY_WINDOWS[this.replayWindow].ms);
      const started = await replayQueries(
        this.instanceId,
        {
          target: this.target,
          from: from.toISOString(),
          to: to.toISOString(),
          limit: Number(this.replayCap),
        },
        { waitSeconds: WAIT_SECONDS },
      );
      this.replayJob = started.job;
      const done = isTerminal(started.job.status)
        ? started.job
        : await watchJob<TestRunResult>(started.job.id, {
            waitSeconds: WAIT_SECONDS,
            onUpdate: (j) => (this.replayJob = j),
          });
      this.replayJob = done;
      const failure = jobFailure(done);
      if (failure) {
        this.replayError = failure;
        return;
      }
      this.replayResult = done.result ?? null;
    } catch (err) {
      this.replayError = parseError(err);
    } finally {
      this.replaying = false;
    }
  }

  /**
   * renderReplay is `SC-F715`'s panel, in its four states.
   *
   * The empty state is the one worth reading twice. A window with no replayable
   * record comes back as a `422` carrying the arithmetic, **not** as a green
   * report, and this panel renders that arithmetic — because "no recorded query in
   * this window can be replayed" and "nothing regressed" are opposite statements
   * and a tidier screen would show the second.
   */
  private renderReplay() {
    return html`<bn-card heading="Query replay">
      <p>
        Replays the queries this instance actually handled against the bundle chosen above, using the
        decision recorded in the audit trail as the baseline. A query whose decision changes is the
        answer; a query that cannot be modeled is reported as such and never counted as unchanged.
      </p>
      <p class="muted">
        <strong>Nothing is executed.</strong> The recorded statement is re-decided inside a one-shot
        container with no network attachment: no datasource is contacted, no rows exist, and the
        recorded row counts are not re-derived. The control plane never runs a customer query.
      </p>
      <div class="form-row">
        <bn-field class="target" label="Window">
          <bn-select
            .options=${REPLAY_WINDOW_OPTIONS}
            .value=${this.replayWindow}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              e.stopPropagation();
              this.replayWindow = e.detail.value;
            }}
          ></bn-select>
        </bn-field>
        <bn-field class="gen" label="Scenario cap">
          <bn-select
            .options=${REPLAY_CAP_OPTIONS}
            .value=${this.replayCap}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              e.stopPropagation();
              this.replayCap = e.detail.value;
            }}
          ></bn-select>
        </bn-field>
        <bn-button
          variant="primary"
          ?loading=${this.replaying}
          ?disabled=${!this.targetValid}
          @click=${() => void this.replay()}
        >
          Replay recorded queries
        </bn-button>
      </div>
      ${this.mayReplay
        ? nothing
        : html`<p class="muted">
            A replay reads recorded SQL, subject identifiers, email addresses and group memberships,
            which the API restricts to a maintainer or owner holding a browser session or a general
            API key. Your role on this organization is ${this.orgRole ?? "unknown"}, so this will be
            refused.
          </p>`}
      ${this.replaying && !this.replayResult
        ? html`<bn-spinner
            block
            size="24"
            label=${this.replayJob
              ? `Replaying: ${this.replayJob.status}`
              : "Selecting recorded queries"}
          ></bn-spinner>`
        : nothing}
      ${this.replayError ? this.renderReplayError(this.replayError) : nothing}
      ${this.replayResult?.replay
        ? this.renderReplayReport(this.replayResult, this.replayResult.replay)
        : nothing}
    </bn-card>`;
  }

  /**
   * renderReplayError shows the refusal with its code and request id, and — for the
   * one refusal that carries arithmetic — the counts that say WHY nothing could be
   * replayed.
   */
  private renderReplayError(err: ApiError) {
    const scanned = err.details?.["records_scanned"];
    const reasons = err.details?.["skipped_by_reason"];
    return html`<div class="replay-error" role="alert">
      <p>${err.message}</p>
      ${scanned !== undefined
        ? html`<bn-kv
            .rows=${[
              { label: "Records scanned", value: String(scanned) },
              { label: "Distinct scenarios", value: String(err.details?.["scenarios_found"] ?? 0) },
              ...(reasons && typeof reasons === "object"
                ? Object.entries(reasons as Record<string, number>).map(([reason, n]) => ({
                    label: SKIP_LABELS[reason] ?? reason,
                    value: String(n),
                  }))
                : []),
            ]}
          ></bn-kv>`
        : nothing}
      <bn-kv
        .rows=${[
          { label: "Error code", value: err.code },
          ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
        ]}
      ></bn-kv>
      <div class="actions">
        <bn-button variant="primary" @click=${() => void this.replay()}>Try again</bn-button>
      </div>
    </div>`;
  }

  /** renderReplayReport is the populated state: the arithmetic, then the scenarios,
   * then the limits — in that order, because the limits qualify the numbers above
   * them and a reader who stops early should have read the numbers first. */
  private renderReplayReport(run: TestRunResult, r: ReplayReport) {
    const changed = r.scenarios.filter((s) => s.verdict === "changed");
    return html`
      <div class="verdict">
        <bn-badge variant=${r.counts.changed > 0 ? "error" : "success"}>
          ${r.counts.changed > 0
            ? `${r.counts.changed} decision${r.counts.changed === 1 ? "" : "s"} would change`
            : "No decision change found"}
        </bn-badge>
        ${r.truncated ? html`<bn-badge variant="warning">truncated at ${r.cap}</bn-badge>` : nothing}
        ${run.sluice_version ? html`<span class="muted">${run.sluice_version}</span>` : nothing}
      </div>
      <bn-kv
        .rows=${[
          { label: "Window", value: `${r.from} → ${r.to}` },
          { label: "Baseline", value: r.baseline },
          { label: "Records scanned", value: String(r.counts.records_scanned) },
          { label: "Distinct scenarios", value: String(r.counts.scenarios_found) },
          { label: "Replayed", value: String(r.counts.replayed) },
          { label: "Unchanged", value: String(r.counts.unchanged) },
          { label: "Changed", value: String(r.counts.changed) },
          { label: "Inconclusive", value: String(r.counts.inconclusive) },
          { label: "Skipped", value: String(r.counts.skipped) },
        ]}
      ></bn-kv>
      ${Object.keys(r.counts.skipped_by_reason ?? {}).length > 0
        ? html`<h2>Not replayed</h2>
            <bn-kv
              .rows=${Object.entries(r.counts.skipped_by_reason ?? {}).map(([reason, n]) => ({
                label: SKIP_LABELS[reason] ?? reason,
                value: String(n),
              }))}
            ></bn-kv>`
        : nothing}
      ${changed.length > 0
        ? html`<h2>Decisions that would change</h2>
            ${changed.map((s) => this.renderScenario(s))}`
        : html`<p class="muted">
            No replayed scenario decides differently under this bundle. That is a statement about
            what was replayed — read the window, the cap and the limits below before reading it as a
            statement about your traffic.
          </p>`}
      ${r.counts.inconclusive > 0
        ? html`<h2>Inconclusive</h2>
            <p class="muted">
              These scenarios produced no comparable decision, so they are neither a change nor a
              pass. The usual cause is a statement the replay could not expand.
            </p>
            ${r.scenarios
              .filter((s) => s.verdict === "inconclusive")
              .map((s) => this.renderScenario(s))}`
        : nothing}
      <h2>What this covered</h2>
      <p class="muted">${r.selection}</p>
      <ul class="limits">
        ${r.limits.map((l) => html`<li>${l}</li>`)}
      </ul>
    `;
  }

  /** renderScenario is one recorded query and what the candidate does with it. The
   * occurrence count is prominent because a change over 4 000 occurrences is a
   * different-sized problem from a change over one. */
  private renderScenario(s: ReplayScenario) {
    return html`<div class="finding">
      <div class="verdict">
        <bn-badge variant=${s.verdict === "changed" ? "error" : "warning"}>${s.verdict}</bn-badge>
        ${s.verdict === "changed"
          ? html`<span
              ><bn-badge variant="allow">${s.recorded_decision}</bn-badge> →
              <bn-badge variant="deny">${s.replayed_decision || "unknown"}</bn-badge></span
            >`
          : nothing}
        <span class="muted"
          >${s.occurrences} occurrence${s.occurrences === 1 ? "" : "s"} · last seen
          ${s.last_seen}</span
        >
      </div>
      <p class="muted">
        ${s.subject}${s.issuer ? ` @ ${s.issuer}` : ""}${s.groups?.length
          ? ` · ${s.groups.join(", ")}`
          : ""}
      </p>
      ${s.sql ? html`<code class="sql">${s.sql}</code>` : nothing}
      ${(s.failures ?? []).map((f) => html`<code class="sql">${f}</code>`)}
      ${s.record_id
        ? html`<a
            class="inline"
            href=${hrefFor("instances", this.instanceId, "audit", s.record_id)}
            >Open the recorded query</a
          >`
        : nothing}
    </div>`;
  }

  /**
   * The other checks in the product, named here because "validation" is the word
   * a reader arrives with and these two are not on this screen. They are not
   * duplicated onto it either: two answers to one question in one surface is how
   * they drift apart.
   */
  private renderOtherChecks() {
    return html`<bn-panel eyebrow="The other checks">
      <p>
        <strong>Datasource specs.</strong> "Validate configuration" on the
        <a class="inline" href=${hrefFor("instances", this.instanceId, "datasources")}
          >datasources tab</a
        >
        confirms that a driver exists for each declared type and reports the declared filters. It
        opens no connection and proves nothing about reachability — connectivity is proven only once
        the instance is running, by its own health loop.
      </p>
      <p>
        <strong>The audit chain.</strong> Verifying that the hash chain is unbroken lives on the
        <a class="inline" href=${hrefFor("instances", this.instanceId, "audit")}>audit tab</a>, because
        it is evidence about records rather than about configuration.
      </p>
      <p class="muted">
        The control-plane linter is not here either: it runs on every save inside the policy editors,
        which is where its findings can point at the field that caused them.
      </p>
    </bn-panel>`;
  }

  private renderForm() {
    const version = this.instance?.sluice_version;
    return html`<bn-card heading="Run the validators">
      <div class="form-row">
        <bn-field class="target" label="Validate">
          <bn-select
            .options=${TARGET_OPTIONS}
            .value=${this.targetKind}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
              this.targetKind = e.detail.value as "draft" | "live" | "generation";
              this.targetError = "";
            }}
          ></bn-select>
        </bn-field>
        ${this.targetKind === "generation"
          ? html`<bn-field
              class="gen"
              label="Generation number"
              required
              .error=${this.targetError}
              hint="A positive integer."
            >
              <bn-input
                inputmode="numeric"
                mono
                .value=${this.generation}
                ?invalid=${!this.targetValid && this.generation !== ""}
                @input=${(e: CustomEvent<{ value: string }>) => {
                  this.generation = e.detail.value;
                  this.targetError = "";
                }}
              ></bn-input>
            </bn-field>`
          : this.targetError
            ? html`<bn-field label="Target" .error=${this.targetError}></bn-field>`
            : nothing}
      </div>

      <div class="checks">
        <bn-checkbox
          label="config validate"
          hint="Loads the rendered server configuration, decodes the policy directory, and runs the whole-bundle cross-checks."
          .checked=${this.commands.includes("config")}
          @change=${(e: CustomEvent<{ checked: boolean }>) =>
            this.toggleCommand("config", e.detail.checked)}
        ></bn-checkbox>
        <bn-checkbox
          label="policy validate"
          hint="Decodes and then COMPILES. This is the only proxied path that reaches the snapshot compiler, so it is the only one that catches a manifest which decodes cleanly and uses a feature this build refuses."
          .checked=${this.commands.includes("policy")}
          @change=${(e: CustomEvent<{ checked: boolean }>) =>
            this.toggleCommand("policy", e.detail.checked)}
        ></bn-checkbox>
        ${this.commands.length === 0
          ? html`<p class="muted" role="status">
              Choose at least one validator. Both is the default, and both is what the publish gate
              runs.
            </p>`
          : nothing}
      </div>

      <div class="actions">
        <bn-button
          variant="primary"
          ?loading=${this.running}
          ?disabled=${this.commands.length === 0 || !this.targetValid}
          @click=${() => void this.run()}
        >
          ${this.running ? "Validating…" : "Run validation"}
        </bn-button>
        ${this.running && this.job
          ? html`<bn-button @click=${() => void this.cancel()}>Cancel</bn-button>`
          : nothing}
        <span class="muted">
          Read-only: it takes no lock on the instance, so it can run while anything else is running.
          ${version ? html`Pinned image: <code>${version}</code>.` : nothing}
        </span>
      </div>
    </bn-card>`;
  }

  /** The in-flight panel. `queued` and `running` are different facts — one is
   * waiting for cluster capacity, the other is a container that started — and a
   * spinner that said only "working" would hide which. */
  private renderRunState() {
    const job = this.job;
    if (!job || !this.running) return nothing;
    return html`<bn-panel eyebrow="Run in progress">
      <div class="actions">
        <bn-spinner
          label=${job.status === "queued"
            ? "Queued — waiting for a slot on the cluster"
            : "Running on the pinned image"}
        ></bn-spinner>
        ${job.progress
          ? html`<span class="muted"
              >${job.progress.step} (${job.progress.index} of ${job.progress.total})</span
            >`
          : nothing}
        <a class="inline" href=${hrefFor("jobs", job.id)}>Job ${job.id}</a>
      </div>
    </bn-panel>`;
  }

  private renderOutcome() {
    if (this.runError) return this.renderRunError(this.runError);
    if (this.result) return this.renderReport(this.result);
    if (this.running) return nothing;
    return this.renderNeverRun();
  }

  private renderNeverRun() {
    return html`<bn-card>
      <bn-empty-state
        icon="✓"
        heading="Nothing validated yet"
        message="Run the validators to check this bundle against the exact binary this instance runs. Publishing runs the same check server-side and refuses a bundle that fails it, so a green report here is what a publish needs — not a convenience."
      >
        <bn-button
          slot="actions"
          variant="primary"
          ?disabled=${this.commands.length === 0}
          @click=${() => void this.run()}
        >
          Run validation
        </bn-button>
      </bn-empty-state>
    </bn-card>`;
  }

  /**
   * A job that reached a non-success terminal state — which is a run that
   * produced **no answer**, and is a different thing from a bundle that failed
   * validation. The remedies differ, so the copy says which one this is.
   *
   * The job's error envelope carries no `request_id` on purpose: a job outlives
   * the request that created it, so that id would be misleading by the time
   * anyone reads the failure (`06` §1.8). The page says that rather than showing
   * a blank field.
   */
  private renderRunError(err: ApiError) {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (this.job) rows.push({ label: "Job", value: this.job.id });
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    const detail = err.details?.["reason"];
    if (typeof detail === "string") rows.push({ label: "Reason", value: detail });
    return html`<bn-card heading="The validation did not produce a report">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv .rows=${rows}></bn-kv>
        ${err.code === "proxy_unavailable"
          ? html`<p class="muted">
              Nothing about your bundle is implicated: the cluster could not start the command. This
              is worth retrying.
            </p>`
          : nothing}
        ${err.code === "instance_error"
          ? html`<p class="muted">
              The command ran and said something this build of Sluice Cloud could not parse. The raw
              stdout and stderr are kept on the job, which is where a support conversation should
              start — a parser that reported "no findings" here would have turned a failing bundle
              into a green light.
            </p>`
          : nothing}
        ${err.requestId
          ? nothing
          : html`<p class="muted">
              A job carries no request id: it outlives the request that started it, so quoting that
              request would point at the wrong thing. Quote the job id instead.
            </p>`}
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.run()}>Try again</bn-button>
          ${this.job
            ? html`<bn-button @click=${() => navigate(hrefFor("jobs", this.job?.id ?? ""))}>
                Open the job
              </bn-button>`
            : nothing}
        </div>
      </div>
    </bn-card>`;
  }

  private renderLoadError(err: ApiError) {
    return html`<bn-card heading="Could not load this instance">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ] as BnKvRow[]}
        ></bn-kv>
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  /* ── the report ────────────────────────────────────────────────────────── */

  private renderReport(r: ValidateResult) {
    const errors = r.counts?.error ?? 0;
    const warnings = r.counts?.warning ?? 0;
    const variant: BnBadgeVariant = r.ok ? "success" : "error";
    return html`
      <bn-card heading="Report">
        <div class="verdict">
          <bn-badge .variant=${variant}>${r.ok ? "Valid" : "Invalid"}</bn-badge>
          <span class="verdict-line">
            ${r.ok
              ? "This bundle passes both stages that ran."
              : `${errors} error${errors === 1 ? "" : "s"}${warnings > 0 ? ` and ${warnings} warning${warnings === 1 ? "" : "s"}` : ""}.`}
          </span>
        </div>
        ${r.ok
          ? nothing
          : html`<p class="muted">
              The run itself worked: the validator started, read the bundle and reported. Exit 3 is a
              correct run with a negative answer, which is why this is a report and not an error
              banner.
            </p>`}
        <bn-kv .rows=${this.reportRows(r)}></bn-kv>
      </bn-card>

      ${this.renderStages(r)} ${this.renderNotes(r)} ${this.renderFindings(r)}
      ${this.renderUnparsed(r)}
    `;
  }

  private reportRows(r: ValidateResult): BnKvRow[] {
    const rows: BnKvRow[] = [
      { label: "Validated", value: targetLabel(r.target), copyable: false },
      { label: "Validators", value: r.commands.join(", "), copyable: false },
    ];
    if (r.target.digest) rows.push({ label: "Bundle digest", value: r.target.digest });
    if (r.reported_digest_prefix) {
      rows.push({
        // Named as a prefix because that is what it is: Sluice prints a 12-character
        // truncation, so it compares against the first 12 characters of the digest
        // above and is not a digest a CI job can assert equality on.
        label: "Digest Sluice printed (12-char prefix)",
        value: r.reported_digest_prefix,
      });
    }
    if (r.objects !== undefined && r.objects > 0) {
      rows.push({ label: "Objects loaded", value: String(r.objects), copyable: false });
    }
    rows.push({
      label: "Server configuration",
      value:
        r.server_config_ok === null
          ? "not checked — config validate did not run"
          : r.server_config_ok
            ? "loaded"
            : "did not load",
      copyable: false,
    });
    if (r.sluice_version) rows.push({ label: "Sluice version", value: r.sluice_version });
    rows.push({ label: "Duration", value: `${r.duration_ms} ms`, copyable: false });
    if (this.job) rows.push({ label: "Job", value: this.job.id });
    for (const id of r.run_ids ?? []) rows.push({ label: "CLI run", value: id });
    return rows;
  }

  /** The stage reached, and what the stages after it did not say. */
  private renderStages(r: ValidateResult) {
    const stage = STAGES[r.stage_reached];
    return html`<bn-panel eyebrow="How far it got">
      <p>
        <strong>${stage ? stage.label : r.stage_reached}</strong> —
        ${stage ? stage.meaning : "This build does not recognise that stage name."}
      </p>
      <p class="muted">
        The stages are sequential and mutually exclusive inside one command, so an empty finding list
        for a later stage means that stage did not run, not that it was satisfied.
      </p>
    </bn-panel>`;
  }

  private renderNotes(r: ValidateResult) {
    const notes = r.notes ?? [];
    if (notes.length === 0) return nothing;
    return html`<bn-card heading="Read the report with these in mind">
      <div class="notes">${notes.map((n) => html`<p>${n}</p>`)}</div>
    </bn-card>`;
  }

  /**
   * Findings, split by **anchoring precision** rather than by severity, because
   * that is the difference that decides what a reader can do next: an
   * object-anchored finding opens an editor, a bundle-level one does not and
   * must not pretend to (`SC-S703`, `SC-S704`).
   */
  private renderFindings(r: ValidateResult) {
    if (r.findings.length === 0) {
      return html`<bn-card heading="Findings">
        <p>
          None. Every manifest the stage above reached decoded, and nothing the validators ran
          objected.
        </p>
      </bn-card>`;
    }
    const anchored = r.findings.filter((f) => f.kind && f.name);
    const bundle = r.findings.filter((f) => !(f.kind && f.name));
    return html`
      ${anchored.length > 0
        ? html`<bn-card heading=${`Findings on objects (${anchored.length})`}>
            <p class="muted">
              Each of these named a kind and a name, so it opens the object that produced it. Sluice
              reports no line numbers — the error format carries one and the loader never fills it in
              — so a finding anchors to an object and, where the message names a field, to that
              field.
            </p>
            ${anchored.map((f) => this.renderFinding(f))}
          </bn-card>`
        : nothing}
      ${bundle.length > 0
        ? html`<bn-card heading=${`Findings on the bundle (${bundle.length})`}>
            <p class="muted">
              These could not be anchored to an object. Compile-stage errors are unstructured
              strings with no file, no kind and no field path, and the paths inside them belong to
              the policy engine rather than to your manifest — so highlighting a form control from
              one would point at something unrelated. They are shown whole instead.
            </p>
            ${bundle.map((f) => this.renderFinding(f))}
          </bn-card>`
        : nothing}
    `;
  }

  private renderFinding(f: ValidationFinding): TemplateResult {
    const severity: BnBadgeVariant = f.severity === "warning" ? "warning" : "error";
    const stage = STAGES[f.stage]?.label ?? f.stage;
    // `?field=` is a HINT for the policy editor: where the validator named a
    // field path, the editor can scroll to and highlight that control. An editor
    // that ignores it still opens the right object, and the path is printed in
    // the row either way — so nothing depends on the hint being honoured.
    const link =
      f.kind && f.name
        ? hrefFor("instances", this.instanceId, "policies", f.kind, f.name) +
          (f.field ? `?field=${encodeURIComponent(f.field)}` : "")
        : undefined;
    return html`<div class="finding">
      <div class="finding-head">
        <bn-badge .variant=${severity}>${f.severity}</bn-badge>
        <span class="chip">${stage}</span>
        <span class="chip">${f.source}</span>
        ${f.kind && f.name
          ? html`<a class="inline" href=${link ?? ""}>${f.kind}/${f.name}</a>`
          : html`<span class="muted">bundle-level</span>`}
        ${f.field ? html`<span class="chip">${f.field}</span>` : nothing}
        ${f.file ? html`<span class="muted">${f.file}</span>` : nothing}
      </div>
      <div class="finding-message">${f.message}</div>
      <div class="finding-raw">${f.raw}</div>
    </div>`;
  }

  /** Output the grammar did not recognise. Loud, because the alternative is a
   * quiet lie (`SC-S704`'s last criterion). */
  private renderUnparsed(r: ValidateResult) {
    const lines = r.unparsed ?? [];
    if (lines.length === 0) return nothing;
    return html`<bn-card heading="Output this build could not structure">
      <div role="alert">
        <p>
          Sluice printed ${lines.length} line${lines.length === 1 ? "" : "s"} that this build's
          parser does not model, so ${lines.length === 1 ? "it is" : "they are"} shown verbatim. The
          report above may be incomplete — treat it as such rather than as a pass.
        </p>
      </div>
      <bn-code
        language="plain"
        label="Unparsed validator output"
        filename="validator-output.txt"
        .value=${lines.join("\n")}
      ></bn-code>
    </bn-card>`;
  }
}

/** targetLabel renders the resolved reference the way the report echoed it. */
function targetLabel(t: { kind: string; generation?: number }): string {
  if (t.kind === "generation") return `generation ${t.generation ?? "?"}`;
  if (t.kind === "live") return `live generation${t.generation ? ` (${t.generation})` : ""}`;
  return t.generation ? `draft (generation ${t.generation})` : "draft";
}

declare global {
  interface HTMLElementTagNameMap {
    "page-validation": PageValidation;
  }
}
